import { ref, computed, type ComputedRef } from 'vue'
import {
  hasLightingEditableField,
  LIGHTING_EDITABLE_FIELD,
  LIGHTING_EFFECT_KIND,
  LIGHTING_TARGET,
  type lightingColor,
  type LightingConfigData,
  type LightingEffectDefinition,
  type RGB,
  type RGBGroup,
  LightingDomainType,
  DeviceError,
  DeviceErrorCode,
  staticType,
  keyMapLighting,
  sanitizeLightingLevel,
} from '@rapoo/device-sdk'
import { useLocaleStore } from '@/stores'
import { defaultReportError } from '@/composables/useErrorLogger'
import { toast } from '@rapoo/element'
import { useLoading } from '@/composables/useLoading'
import { createCoalesceLatestRequestQueue } from '@/utils/coalesceLatestRequest'
import {
  useLightingSDK,
  beginLatest,
  createLatestCommitter,
  useLightingCapability,
  useLightingLifecycle,
} from './useLightingSDK'

/**
 * 按键灯（LIGHTING_TARGET.Keyboard）业务。
 *
 * - 对外：状态 + set* + init
 * - 参数：getConfig / setConfig
 * - 虚拟键盘直接绑本 hook 状态（不再维护 keyboardPreview* 副本）
 * - 亮度/速度/灯效硬件上报只在此注册（侧灯/指示灯不听）
 */

export type UseKeyLightingOptions = {
  autoInit?: boolean
}

/**
 * 动态灯效 id 的起始值。
 *
 * 与 SDK 侧 `dynamicDomain.normalizeIndex = (i) => Math.max(0, i - 100)` 是同一个约定：
 * id >= 100 落动态领域，否则落静态领域。
 */
const DYNAMIC_EFFECT_ID_BASE = 100

/**
 * 解析灯效所属领域。
 *
 * 不能在 kind 缺失时无脑回落 Dynamic：`normalizeIndex` 会把静态 id（如 3）压成 0，
 * 而 `getAddress` 越界时又静默回退到第一个元素，两者叠加会把参数
 * **静默写进动态灯效 0 的槽位**，污染另一个灯效。
 * 所以 kind 未知时退回按 id 判定，与 SDK 的索引约定保持一致。
 */
function resolveDomain(kind?: string, effectId?: number): LightingDomainType {
  if (kind === LIGHTING_EFFECT_KIND.Static) return LightingDomainType.Static
  if (kind === LIGHTING_EFFECT_KIND.Dynamic) return LightingDomainType.Dynamic
  return (effectId ?? 0) >= DYNAMIC_EFFECT_ID_BASE
    ? LightingDomainType.Dynamic
    : LightingDomainType.Static
}

/** 调色板兜底色：新建的自定义灯效在固件里就是纯白 */
const DEFAULT_PALETTE_RGB: RGB = { r: 255, g: 255, b: 255 }

/**
 * 逐键灯效没有单一"灯效颜色"，取出现次数最多的按键色作为调色板当前色。
 *
 * 纯黑代表"该键不亮"而非用户选过的颜色，参与统计会让整块调色板发黑，因此跳过；
 * 全部为黑（例如逐键区还没写过）时回退到白色。
 */
function dominantKeyColor(keys: keyMapLighting): RGB {
  const tally = new Map<string, { rgb: RGB; count: number }>()

  for (const key of keys) {
    const color = key.rgb
    if (!color) continue
    if (color.r === 0 && color.g === 0 && color.b === 0) continue

    const tallyKey = `${color.r},${color.g},${color.b}`
    const hit = tally.get(tallyKey)
    if (hit) hit.count += 1
    else tally.set(tallyKey, { rgb: { ...color }, count: 1 })
  }

  let best: { rgb: RGB; count: number } | undefined
  for (const entry of tally.values()) {
    if (!best || entry.count > best.count) best = entry
  }
  return best?.rgb ?? DEFAULT_PALETTE_RGB
}

function nextCustomEffectId(list: LightingEffectDefinition[]): number {
  const valueSet = new Set(list.map((e) => e.id).filter((v) => v < 100))
  let i = 0
  while (i < 100 && valueSet.has(i)) i++
  return i
}

export function useKeyLighting(options: UseKeyLightingOptions = {}) {
  const { autoInit = true } = options
  const { t } = useLocaleStore()
  const { isLoading, withLoading } = useLoading()
  const { currentDevice, hasBacklightSwitch } = useLightingCapability()

  const {
    getEffects,
    getConfig,
    setConfig,
    getKeysColorStream,
    setKeysColor,
    getCurrentEffect,
    setCurrentEffect,
    getBacklightSwitch,
    setBacklightSwitch,
    createCustomEffect,
    deleteCustomEffect,
    onBrightnessChanged,
    offBrightnessChanged,
    onSpeedChanged,
    offSpeedChanged,
    onEffectChanged,
    offEffectChanged,
    getLighting,
  } = useLightingSDK()

  const rgb = ref<RGB>({ r: 255, g: 255, b: 255 })
  const rgbGroup = ref<RGBGroup>([
    { r: 0, g: 0, b: 0 },
    { r: 255, g: 255, b: 255 },
  ])
  const gradientDirection = ref(1)
  /** 动态灯效运动方向（0=正向，1=反向），GCOME_AP 波浪/扩散/回旋类灯效使用。 */
  const direction = ref(0)
  const isColorful = ref(false)
  const effects = ref<LightingEffectDefinition[]>([])
  const currentMode = ref(-1)
  const brightness = ref(60)
  const speed = ref(60)
  const keyMapRGB = ref<keyMapLighting>([])
  const enabledBacklight = ref<boolean | null>(null)

  const effectConfRequestId = ref(0)
  const initRequestId = ref(0)
  const modeRequestId = ref(0)

  /**
   * 正在进行的下发数量。
   *
   * 设备在写入后的 settle 窗口（setCurrentEffect 后有 2s 延时）里会持续上报
   * 0x01 用户数据，其中的瞬态值会覆盖 brightness/speed/currentMode，而这些 ref
   * 正是 commitConfig 构造写入载荷时读取的来源——于是坏值被固化进 Flash。
   * 写入期间一律忽略上报，把「本端回声」和「用户在硬件上的真实操作」区分开。
   */
  const pendingWrites = ref(0)

  /** 写入串行化链：避免多个「读-改-写」并发读到同一份缓存块后互相覆盖 */
  let writeChain: Promise<unknown> = Promise.resolve()

  /**
   * 上一次加载完成的灯效 id。
   *
   * 用来区分「切换到另一个灯效」和「上色后重下发同一个灯效」：前者要按灯效自身的
   * 按键颜色重算调色板，后者必须保留用户刚选的画笔色，否则每涂一次就被按键主色顶回去。
   */
  let lastLoadedEffectId: number | undefined

  /** 把设备写入操作串行化，并在此期间挂起上报同步 */
  const runExclusive = <T>(task: () => Promise<T>): Promise<T | undefined> => {
    const next = writeChain.then(async () => {
      pendingWrites.value++
      try {
        return await task()
      } finally {
        pendingWrites.value--
      }
    })
    // 链上任何一次失败都不能卡死后续写入
    writeChain = next.catch(() => undefined)
    return next
  }

  const currentEffectInfo = computed(() =>
    effects.value.find((e) => e.id === currentMode.value),
  ) as ComputedRef<LightingEffectDefinition | undefined>

  /** 当前灯效的速度调节范围（默认 0-100 连续；GCOME_AP 等档位设备从配置读取）。 */
  const speedRange = computed(
    () => currentEffectInfo.value?.speedRange ?? { min: 0, max: 100, step: 1 },
  )

  /** 当前灯效的亮度调节范围（默认 0-100 连续；GCOME_AP 为 0-100 步进 25 的 5 档）。 */
  const brightnessRange = computed(
    () => currentEffectInfo.value?.brightnessRange ?? { min: 0, max: 100, step: 1 },
  )

  const resetUi = () => {
    effects.value = []
    keyMapRGB.value = []
    enabledBacklight.value = null
    currentMode.value = -1
    lastLoadedEffectId = undefined
  }

  /**
   * 解析当前灯效的炫彩 UI 状态。
   *
   * 仅 ColorCycle、不可改 Color 的灯效本身就是炫彩（如 ESK 波纹、GCOME 光谱/百花），
   * 固件共享配置区可能残留上一个单色灯效的 isColorful=false，不能原样采用。
   */
  const resolveIsColorful = (
    data: LightingConfigData,
    mode?: LightingEffectDefinition,
  ): boolean => {
    if (!mode) return data.isColorful ?? false
    const hasColor = hasLightingEditableField(mode, LIGHTING_EDITABLE_FIELD.Color)
    const hasColorCycle = hasLightingEditableField(mode, LIGHTING_EDITABLE_FIELD.ColorCycle)
    if (hasColorCycle && !hasColor) return true
    if (hasColorCycle) return data.isColorful ?? false
    return false
  }

  const applyConfig = (data: LightingConfigData, mode?: LightingEffectDefinition) => {
    brightness.value = sanitizeLightingLevel(data.brightness)

    if (!mode || mode.kind === LIGHTING_EFFECT_KIND.Dynamic) {
      speed.value = sanitizeLightingLevel(data.speed)
      isColorful.value = resolveIsColorful(data, mode)
      rgb.value = data.rgb ?? rgb.value
      gradientDirection.value = 1
      direction.value = data.direction ? 1 : 0
      keyMapRGB.value = []
      return
    }

    if (mode.kind === LIGHTING_EFFECT_KIND.Static) {
      const direction = data.gradientDirection
      if (direction === 0) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Invalid gradient direction')
      }
      gradientDirection.value = direction ?? 1
      isColorful.value = false

      const hasColor = hasLightingEditableField(mode, LIGHTING_EDITABLE_FIELD.Color)
      if (gradientDirection.value === 1) {
        // 自定义逐键灯效的 config rgb 只是固件占位白，真实颜色存在逐键 RGB 区，
        // 回读它只会把调色板刷成白色。这类灯效的调色板由 loadEffectConf 在
        // 逐键颜色加载完之后单独推导。
        if (hasColor && !mode.isCustom) rgb.value = data.rgb ?? rgb.value
        isColorful.value = resolveIsColorful(data, mode)
      }
      if ([2, 3].includes(gradientDirection.value)) {
        rgbGroup.value = data.rgbGroup ?? rgbGroup.value
      }
    }
  }

  const loadEffectConf = async (index: number, stillWanted?: () => boolean) => {
    const latest = beginLatest(effectConfRequestId)
    // 除「被更晚的加载取代」外，调用方还可声明「结果已无人需要」（亮度 / 速度连击中的旧回读），
    // 否则回读会把旧值闪回滑块
    const task = { alive: () => latest.alive() && (stillWanted?.() ?? true) }
    if (!getLighting() || index < 0) return

    // 只有真正切换灯效（含首次进入页面）时才重算调色板；上色后的重下发走的是
    // 同一个 id，此时必须保留用户刚选的画笔色
    const isEffectSwitch = index !== lastLoadedEffectId

    const mode = effects.value.find((e) => e.id === index)
    try {
      if (!mode) {
        // effects 尚未加载出该灯效时，按 id 判定领域，不能一律当动态处理
        const data = await getConfig(resolveDomain(undefined, index), index)
        if (!task.alive()) return
        brightness.value = sanitizeLightingLevel(data?.brightness)
        speed.value = sanitizeLightingLevel(data?.speed)
        isColorful.value = false
        gradientDirection.value = 1
        direction.value = 0
        keyMapRGB.value = []
        return
      }

      const domain = resolveDomain(mode.kind, index)
      const data = await getConfig(domain, index)
      if (!task.alive() || !data) return
      applyConfig(data, mode)

      if (mode.kind === LIGHTING_EFFECT_KIND.Static) {
        // Static 配置区不含 speed。仅当该灯效可编辑速度时才去 Dynamic 槽位再读；
        // 常量 / 上下左右渐变等无 Speed 字段时跳过，避免切效后立刻连读 busy。
        if (hasLightingEditableField(mode, LIGHTING_EDITABLE_FIELD.Speed)) {
          const speedConf = await getConfig(LightingDomainType.Dynamic, DYNAMIC_EFFECT_ID_BASE)
          if (!task.alive()) return
          speed.value = sanitizeLightingLevel(speedConf?.speed)
        }

        keyMapRGB.value = []
        if (data.gradientDirection === 1) {
          if (mode.isCustom) {
            // 自定义逐键：真实颜色在逐键 RGB 区，必须流式回读
            const stream = getKeysColorStream(LightingDomainType.Static, index)
            if (stream) {
              for await (const keyRgb of stream) {
                if (!task.alive()) return
                keyMapRGB.value.push({ ...keyRgb })
              }
            }
            // 逐键加载完再推导调色板，避免沿用上一灯效残留色
            if (isEffectSwitch) {
              rgb.value = dominantKeyColor(keyMapRGB.value)
            }
          } else if (data.rgb) {
            // 预设常量：首包 getConfig 已含单色。本地铺满键位即可，
            // 勿再走 getKeysColorStream（内部 getProperty→又一次完整 getConfig）。
            const keys = currentDevice.value?.manifest.layout.keys.flat() ?? []
            const color = { r: data.rgb.r, g: data.rgb.g, b: data.rgb.b }
            keyMapRGB.value = keys.map((k) => ({ index: k.key, rgb: color }))
          }
        }
      }

      if (task.alive()) lastLoadedEffectId = index
    } catch (error) {
      console.error('Failed to load key lighting config:', error)
      defaultReportError(`Failed to load key lighting config: ${JSON.stringify(error)}`)
    }
  }

  /**
   * 重新下发当前灯效并回读参数，让固件立即应用刚写入的配置。
   *
   * 入参锁定调用方开始时的灯效 id：期间硬件上报可能已经改了 currentMode，
   * 若在这里再读一次 currentEffectInfo，就会把用户的操作切到别的灯效上去。
   */
  const reapplyCurrentMode = async (effectId = currentMode.value, stillWanted?: () => boolean) => {
    if (effectId < 0) return
    await setCurrentEffect(effectId)
    // 重下发期间已有更新的同字段值在等：这次回读只会把旧值闪回 UI，直接跳过
    if (stillWanted && !stillWanted()) return
    await loadEffectConf(effectId, stillWanted)
  }

  const init = async (opts?: { force?: boolean }) => {
    if (!currentDevice.value || !getLighting()) return

    const task = beginLatest(initRequestId)
    effectConfRequestId.value++

    await withLoading(
      async () => {
        try {
          effects.value = await getEffects(LIGHTING_TARGET.Keyboard)
          if (!task.alive()) return

          if (hasBacklightSwitch.value) {
            enabledBacklight.value = (await getBacklightSwitch()) === 1
            if (!task.alive()) return
          }

          const effectId = await getCurrentEffect()
          if (effectId !== undefined) currentMode.value = effectId
          if (!task.alive()) return

          await loadEffectConf(currentMode.value)
        } catch (err) {
          console.error('Failed to initialize key lighting:', err)
          defaultReportError(`Failed to initialize key lighting: ${JSON.stringify(err)}`)
        }
      },
      { skipIfLoading: opts?.force ? false : true },
    )
  }

  const setMode = async (mode?: LightingEffectDefinition) => {
    if (!getLighting() || !mode) return
    keyMapRGB.value = []
    currentMode.value = mode.id

    // 记号在 withLoading 之外领取：连续切换时，先发起的那次必须能感知到自己已被取代
    const task = beginLatest(modeRequestId)

    // skipIfLoading: false 是关键。withLoading 的旧版分支按函数源码去重，
    // 这里的箭头函数源码恒定，默认行为会让 2s settle 窗口内的第二次切换
    // 连函数体都进不去——但 currentMode 已经改了，于是 UI 停在新灯效、
    // 硬件停在旧灯效，参数也是旧灯效的。
    await withLoading(
      async () => {
        await runExclusive(async () => {
          try {
            await setCurrentEffect(mode.id)
            // 已被更晚的切换取代时就此打住，避免用旧灯效的参数覆盖 UI
            if (!task.alive()) return
            await loadEffectConf(mode.id)
          } catch (err) {
            console.error('Failed to set key lighting mode:', err)
            defaultReportError(`Failed to set key lighting mode: ${JSON.stringify(err)}`)
          }
        })
      },
      { skipIfLoading: false },
    )
  }

  const setCustomMode = async (
    mode: { event: string; type: number; name: string },
    index?: number,
  ) => {
    if (!getLighting()) return

    await withLoading(async () => {
      await runExclusive(async () => {
        try {
          if (mode.event === 'add') {
            const targetValue = nextCustomEffectId(effects.value)
            if (targetValue > 15) {
              throw new DeviceError(
                DeviceErrorCode.MAX_LIMIT_REACHED,
                'Max custom mode count is 15',
              )
            }
            await createCustomEffect(
              { name: mode.name, staticType: mode.type as staticType },
              targetValue,
            )
            currentMode.value = targetValue

            // 先就地清空，避免 setCurrentEffect 的 settle 加重载这段时间里，
            // 调色板和虚拟键盘还残留着上一个灯效的颜色。
            // 随后的 init 会按新灯效的逐键颜色重新推导调色板。
            rgb.value = DEFAULT_PALETTE_RGB
            keyMapRGB.value = []
          }
          if (mode.event === 'delete' && index !== undefined) {
            const defaultModel = await deleteCustomEffect(index)
            effects.value = effects.value.filter((effect) => effect.id !== index)
            if (defaultModel !== undefined) currentMode.value = defaultModel
          }
        } catch (error) {
          if (error instanceof DeviceError && error.code === DeviceErrorCode.MAX_LIMIT_REACHED) {
            toast.error(t('toast.light.maxLimit'))
            return
          }
          console.error('Failed to set custom mode:', error)
          defaultReportError(`Failed to set custom mode: ${JSON.stringify(error)}`)
        } finally {
          if (currentMode.value >= 0) await setCurrentEffect(currentMode.value)
          await init({ force: true })
        }
      })
    })
  }

  /**
   * 逐键 RGB 下发的内部实现，不含独占与 reapply。
   * setColor 会复用它，所以这里不能再进 runExclusive，否则会自等待死锁。
   */
  const applyKeyMapColor = async (keyMap: keyMapLighting, _effectId: number) => {
    const info = currentEffectInfo.value
    if (!hasLightingEditableField(info, LIGHTING_EDITABLE_FIELD.Color)) return
    if (info?.kind !== LIGHTING_EFFECT_KIND.Static || !info.isCustom) return

    const map = keyMap.map((item) => {
      const matched = keyMapRGB.value.find((key) => key.index === item.index)
      return { ...matched, ...item, rgb: item.rgb ?? rgb.value }
    })

    try {
      await setKeysColor(LightingDomainType.Static, map, currentMode.value)
      // 写入成功后同步内存键位色
      keyMapRGB.value = keyMapRGB.value.map((item) => {
        const changed = map.find((key) => key.index === item.index)
        return changed ? { ...item, rgb: changed.rgb } : item
      })
    } catch (error) {
      console.error('Failed to set key map color:', error)
      defaultReportError(`Failed to set key map color: ${JSON.stringify(error)}`)
    } finally {
      // 必须重新设置一次当前灯效模式，否则部分固件不会重新应用 LED 定义区配置。
      // 只重设模式（必要部分），跳过 loadEffectConf 的全量回显读取：
      // keyMapRGB 已在上面内存同步，配置未变化，无需重新读取固件。
      const mode = currentEffectInfo.value
      if (mode) {
        await setCurrentEffect(mode.id)
      }
    }
  }

  const setColor = async (color: lightingColor, selectedKeys: keyMapLighting = []) => {
    if (!getLighting()) return
    const info = currentEffectInfo.value
    const canColor = hasLightingEditableField(info, LIGHTING_EDITABLE_FIELD.Color)
    const canCycle = hasLightingEditableField(info, LIGHTING_EDITABLE_FIELD.ColorCycle)
    const canGradient = hasLightingEditableField(info, LIGHTING_EDITABLE_FIELD.Gradient)
    if (!canColor && !canCycle && !canGradient) return

    if (color === 'linear' && !canCycle) {
      console.warn('Current effect does not support color cycle lighting')
      return
    }
    isColorful.value = Boolean(canCycle && color === 'linear')

    // 灯效 id 在入口锁定：期间硬件上报可能改了 currentMode，
    // 若在下发时才读，颜色会被写进另一个灯效的槽位
    const effectId = currentMode.value
    if (effectId < 0) return
    const domain = resolveDomain(info?.kind, effectId)

    await withLoading(async () => {
      await runExclusive(async () => {
        // 未选中按键时会提前返回，此时没有任何下发，不必再重下发灯效
        let didWrite = true
        try {
          if (info?.kind === LIGHTING_EFFECT_KIND.Dynamic) {
            if (isColorful.value && canCycle) {
              await setConfig(domain, effectId, { isColorful: true })
            } else if (canColor) {
              await setConfig(domain, effectId, { isColorful: false, rgb: rgb.value })
            }
          } else if (info?.kind === LIGHTING_EFFECT_KIND.Static) {
            const canSetCustomKeyMap =
              info.isCustom && !hasLightingEditableField(info, LIGHTING_EDITABLE_FIELD.Gradient)
            if (canSetCustomKeyMap) {
              if (selectedKeys.length === 0) {
                didWrite = false
                toast.error(t('toast.light.noSelect'))
                return
              }
              await applyKeyMapColor(selectedKeys, effectId)
            } else if (canGradient) {
              await setConfig(LightingDomainType.Static, effectId, {
                rgbGroup: rgbGroup.value,
              })
            } else if (canColor) {
              await setConfig(LightingDomainType.Static, effectId, {
                rgb: rgb.value,
                isColorful: isColorful.value,
              })
            }
          }
        } catch (error) {
          console.error('Failed to set color:', error)
          defaultReportError(`Failed to set color: ${JSON.stringify(error)}`)
        } finally {
          if (didWrite) await reapplyCurrentMode(effectId)
        }
      })
    })
  }

  /**
   * 同字段（label）合并写入：滑块连点只落最后一次。
   * runExclusive 是严格 FIFO，若每次松手都直接入链，设备会把每个中间值依次写一遍、
   * 再逐个回读进 UI——滑块看起来在慢慢回放历史值。
   */
  const commitLatest = createLatestCommitter<
    string,
    { partial: Partial<LightingConfigData>; effectId: number; domain: LightingDomainType }
  >(async (label, { partial, effectId, domain }, { hasNewer, settle }) => {
    // 整轮（含静默等待）都占着写链：期间的硬件上报按回声处理，不会触发一次多余回读
    await runExclusive(async () => {
      try {
        await setConfig(domain, effectId, partial)
      } catch (err) {
        console.error(`Failed to set ${label}:`, err)
        defaultReportError(`Failed to set ${label}: ${JSON.stringify(err)}`)
      } finally {
        // 连击中先静默一小段；期间来了更新值，这一轮就不重下发 / 回读，交给最后一轮。
        // 否则设备会滞后地把每个值都亮一遍，回读还会把旧值闪回滑块
        if (!(await settle())) await reapplyCurrentMode(effectId, () => !hasNewer())
      }
    })
  })

  const commitConfig = async (
    partial: Partial<LightingConfigData>,
    label: string,
    domain?: LightingDomainType,
  ) => {
    if (!getLighting()) return

    // 灯效 id 与领域在入口一次性锁定，后续上报改动 currentMode 不会让这次写入跑偏
    const effectId = currentMode.value
    if (effectId < 0) return
    const targetDomain = domain ?? resolveDomain(currentEffectInfo.value?.kind, effectId)

    await commitLatest(label, { partial, effectId, domain: targetDomain })
  }

  const setBrightness = async (value?: number) => {
    // 越界值绝不能进入写入载荷：一次坏读会被「读-改-写」固化进 Flash
    if (value !== undefined) brightness.value = sanitizeLightingLevel(value, brightness.value)
    await commitConfig({ brightness: brightness.value }, 'brightness')
  }

  const setSpeed = async (value?: number) => {
    if (value !== undefined) speed.value = sanitizeLightingLevel(value, speed.value)
    await commitConfig({ speed: speed.value }, 'speed')
  }

  const setGradient = async (data: { rgbGroup: RGBGroup; direction: number }) => {
    withLoading(async () => {
      rgbGroup.value = data.rgbGroup
      gradientDirection.value = data.direction
      await commitConfig(
        { gradientDirection: data.direction, rgbGroup: data.rgbGroup },
        'gradient',
        LightingDomainType.Static,
      )
    })
  }

  /** 设置动态灯效运动方向（0=正向，1=反向）。 */
  const setDirection = async (value: number) => {
    direction.value = value ? 1 : 0
    await commitConfig({ direction: direction.value }, 'direction')
  }

  const setBacklightEnabled = async (enable: boolean) => {
    if (!currentDevice.value || !hasBacklightSwitch.value || !getLighting()) return
    await withLoading(async () => {
      await runExclusive(async () => {
        try {
          await setBacklightSwitch(enable)
          enabledBacklight.value = enable
        } catch (error) {
          console.error('Failed to set backlight switch:', error)
          defaultReportError(`Failed to set backlight switch: ${JSON.stringify(error)}`)
        }
      })
    })
  }

  /**
   * 音乐律动占用 HID（0xdd）期间挂起按键灯同步。
   *
   * GAME_MODE=0 与「灯光自定义」是同一个固件模式。律动切 0 时 0xA3 会上报
   * EffectChanged(0)，若这里当自定义灯去 GetLedDefine/GetFunc，会和 0xdd 抢通道，
   * 真机粉色渐变里就会闪出蓝/红/紫/绿色块。测试页没有本 hook，所以不会中招。
   */
  let hardwareSyncPaused = false

  /** 上报触发的灯效回读：最多 1 in-flight + 1 pending；结束后再静默 280ms 才跑 pending */
  const effectReportFetchQueue = createCoalesceLatestRequestQueue('keyLighting.effectReport', {
    trailingDelayMs: 280,
  })

  const pauseHardwareSync = () => {
    hardwareSyncPaused = true
    effectReportFetchQueue.clearPending()
    effectConfRequestId.value++
    initRequestId.value++
    modeRequestId.value++
  }

  const resumeHardwareSync = () => {
    hardwareSyncPaused = false
  }

  /**
   * 本端正在下发时，设备回来的上报是我们自己写入的回声（或固件 settle 期间的瞬态值），
   * 不能拿它覆盖 UI ——那些 ref 正是下一次写入载荷的来源。
   */
  const isEchoReport = () => pendingWrites.value > 0

  const onBrightnessReport = (blLevel: number) => {
    if (hardwareSyncPaused || isEchoReport()) return
    brightness.value = sanitizeLightingLevel(blLevel, brightness.value)
  }

  const onSpeedReport = (blSpeed: number) => {
    if (hardwareSyncPaused || isEchoReport()) return
    speed.value = sanitizeLightingLevel(blSpeed, speed.value)
  }

  /**
   * 硬件侧灯效切换（FN 灯效切换键）同步。
   *
   * 只读不写：这里绝不能调 setCurrentEffect，否则会和硬件来回顶。
   * 回读走 coalesce 队列：快速连按 FN 时只保留进行中的一次 + 最新一次。
   */
  const onEffectReport = (blMode: number) => {
    if (hardwareSyncPaused || isEchoReport()) return
    if (!Number.isFinite(blMode) || blMode < 0) return
    if (blMode === currentMode.value) return

    currentMode.value = blMode
    keyMapRGB.value = []

    // 硬件可能切到 UI 尚未加载的自定义灯效，此时先把灯效列表补齐
    if (!effects.value.some((e) => e.id === blMode)) {
      effectReportFetchQueue.enqueue(async () => {
        // 执行时以当时的 currentMode 为准（连切期间闭包里的 blMode 可能已过时）
        if (!effects.value.some((e) => e.id === currentMode.value)) {
          await init({ force: true })
          return
        }
        await loadEffectConf(currentMode.value)
      })
      return
    }
    effectReportFetchQueue.enqueue(async () => {
      await loadEffectConf(currentMode.value)
    })
  }

  useLightingLifecycle({
    autoInit,
    reset: resetUi,
    init,
    onDevice: (device) => {
      onBrightnessChanged(onBrightnessReport, device)
      onSpeedChanged(onSpeedReport, device)
      onEffectChanged(onEffectReport, device)
    },
    onDeviceLeave: (device) => {
      offBrightnessChanged(onBrightnessReport, device)
      offSpeedChanged(onSpeedReport, device)
      offEffectChanged(onEffectReport, device)
    },
    onInvalidate: () => {
      initRequestId.value++
      effectConfRequestId.value++
      modeRequestId.value++
    },
  })

  return {
    isLoading,
    hasBacklightSwitch,
    enabledBacklight,
    effects,
    currentMode,
    currentEffectInfo,
    rgb,
    rgbGroup,
    isColorful,
    brightness,
    speed,
    speedRange,
    brightnessRange,
    gradientDirection,
    direction,
    keyMapRGB,
    init,
    setMode,
    setColor,
    setBrightness,
    setSpeed,
    setGradient,
    setDirection,
    setCustomMode,
    setBacklightEnabled,
    pauseHardwareSync,
    resumeHardwareSync,
  }
}
